<template>
  <div
    class="p-3 bg-fill-brand-secondary rounded-xl flex-col justify-end items-end gap-3 inline-flex w-full"
  >
    <!-- Action Type Toggle -->
    <div class="self-stretch flex justify-center items-center gap-1">
      <button
        :class="[
          'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1',
          actionType === 'app'
            ? 'bg-fill-brand text-white'
            : 'bg-surface text-white/60 hover:text-white',
        ]"
        @click="actionType = 'app'"
      >
        {{ $t("channel.leaderboard.rewardUser.appActions") }}
      </button>
      <button
        :class="[
          'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1',
          actionType === 'channel'
            ? 'bg-fill-brand text-white'
            : 'bg-surface text-white/60 hover:text-white',
          !channelStore.permissions.manageChannel &&
            'opacity-50 cursor-not-allowed',
        ]"
        :disabled="!channelStore.permissions.manageChannel"
        @click="
          channelStore.permissions.manageChannel && (actionType = 'channel')
        "
      >
        {{ $t("channel.leaderboard.rewardUser.channelActions") }}
      </button>
    </div>

    <!-- Action Selection -->
    <div
      class="self-stretch h-[68px] flex-col justify-start items-start gap-1 flex"
    >
      <div class="self-stretch justify-start items-center gap-2 inline-flex">
        <div
          class="grow shrink basis-0 text-white text-sm font-medium font-sans leading-tight"
        >
          {{
            actionType === "app"
              ? $t("channel.leaderboard.rewardUser.selectAppAction")
              : $t("channel.leaderboard.rewardUser.selectChannelAction")
          }}
        </div>
        <div
          class="text-white/80 text-xs font-semibold font-sans leading-[18px]"
        >
          {{ $t("channel.leaderboard.rewardUser.required") }}
        </div>
      </div>
      <div
        class="self-stretch h-11 px-4 bg-surface rounded-lg border-2 border-fill-brand justify-start items-center gap-2 inline-flex"
      >
        <select
          v-model="selectedAction"
          class="grow shrink basis-0 text-white text-sm font-medium font-sans leading-tight bg-transparent"
        >
          <option value="" disabled>
            {{
              actionType === "app"
                ? $t("channel.leaderboard.rewardUser.selectAnAppAction")
                : $t("channel.leaderboard.rewardUser.selectAChannelAction")
            }}
          </option>
          <template v-if="actionType === 'app'">
            <option
              v-for="action in appActions"
              :key="action.name"
              :value="action.name"
            >
              {{ action.title }} ({{ action.points }}pts)
            </option>
          </template>
          <template v-else>
            <option
              v-for="action in channelActions"
              :key="action.id"
              :value="action.id"
            >
              {{ action.title }} ({{ action.points }}pts)
            </option>
          </template>
        </select>
      </div>
    </div>

    <!-- Apply Button -->
    <button
      class="self-stretch h-10 px-6 py-1 bg-fill-brand rounded-lg border-t border-white/40 justify-center items-center gap-1 inline-flex text-center text-white text-sm font-medium font-sans leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
      type="button"
      :disabled="!selectedAction"
      @click="onApplyPoints"
    >
      {{ $t("channel.leaderboard.rewardUser.applyPoints") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  GetAppActionsDocument,
  type FindByUserResultFragment,
} from "@/generated/graphql";
import { ref, computed, watch } from "vue";
import { useQuery, useMutation } from "@vue/apollo-composable";
import { useToastStore } from "@/stores/toast.store";
import { graphql as gql } from "@/generated";
import { useChannelStore } from "@/stores/channel.store";
import { useI18n } from "vue-i18n";

const GetChannelActionsDocument = gql(/* GraphQL */ `
  query GetChannelActionsForReward {
    getChannelActions {
      ... on ChannelActionForChannelAdminList {
        list {
          id
          title
          short_title
          points
        }
      }
    }
  }
`);

const RewardUserDocument = gql(/* GraphQL */ `
  mutation RewardUser($user_id: String!, $app_action_name: String!) {
    rewardUser(user_id: $user_id, app_action_name: $app_action_name) {
      ... on AppActionSuccess {
        success
      }
    }
  }
`);

const RewardUserWithChannelActionDocument = gql(/* GraphQL */ `
  mutation RewardUserWithChannelAction(
    $user_id: String!
    $channel_action_id: String!
  ) {
    rewardUserWithChannelAction(
      user_id: $user_id
      channel_action_id: $channel_action_id
    ) {
      ... on ChannelActionSuccess {
        success
      }
    }
  }
`);

const { t } = useI18n();
const actionType = ref<"app" | "channel">("app");
const selectedAction = ref<string | null>(null);
const toastStore = useToastStore();
const channelStore = useChannelStore();

const props = defineProps<{
  selectedUser: FindByUserResultFragment;
}>();

const emit = defineEmits<{
  reset: [];
}>();

const { result: appActionsResult } = useQuery(GetAppActionsDocument);
const { result: channelActionsResult } = useQuery(
  GetChannelActionsDocument,
  {},
  {
    enabled: computed(() => channelStore.permissions.manageChannel),
  },
);
const { mutate: rewardUser } = useMutation(RewardUserDocument);
const { mutate: rewardUserWithChannelAction } = useMutation(
  RewardUserWithChannelActionDocument,
);

const appActions = computed(() => {
  const result = appActionsResult.value?.getAppActions;
  return result && "list" in result ? result.list : [];
});
const channelActions = computed(() => {
  const result = channelActionsResult.value?.getChannelActions;
  return result && "list" in result ? result.list : [];
});

// Reset selected action when switching between action types
watch(actionType, (newType) => {
  selectedAction.value = null;

  // If switching to channel actions but user doesn't have permission, switch back to app
  if (newType === "channel" && !channelStore.permissions.manageChannel) {
    actionType.value = "app";
  }
});

const onApplyPoints = async () => {
  if (!selectedAction.value) return;

  try {
    if (actionType.value === "app") {
      await rewardUser({
        user_id: props.selectedUser.id,
        app_action_name: selectedAction.value,
      });
    } else {
      await rewardUserWithChannelAction({
        user_id: props.selectedUser.id,
        channel_action_id: selectedAction.value,
      });
    }

    channelStore.lastLeaderboardUpdate = Date.now();

    toastStore.addToast({
      title: t("channel.leaderboard.rewardUser.pointsAwarded"),
      message:
        actionType.value === "app"
          ? t(
              "channel.leaderboard.rewardUser.pointsAwardedSuccessfullyAppAction",
            )
          : t(
              "channel.leaderboard.rewardUser.pointsAwardedSuccessfullyChannelAction",
            ),
      type: "success",
    });
    emit("reset");
  } catch (error) {
    console.error("Failed to award points:", error);
    toastStore.addToast({
      title: t("channel.leaderboard.rewardUser.error"),
      message: t("channel.leaderboard.rewardUser.pointsAwardedFailed"),
      type: "error",
    });
  }
};
</script>
